// «Cantidad para unidades distintas» (Ronda 2, como Random): al llegar a la cantidad de un producto
// que tiene caja (u otra presentación secundaria), el ejecutivo escribe UNIDADES o CAJAS y el otro
// campo se calcula con la R.T.U. (razón de transformación = factor de la caja). La línea se guarda
// SIEMPRE en unidad base. A diferencia de Random no hay «desactivar razón de transformación»: la
// unidad base es la autoridad (decisión 2026-10-03) y una caja vale siempre factor × unidad.
//
// Teclado (como Random): el foco parte en Unidad primaria; Enter ahí baja a Unidad secundaria (con
// las cajas ya calculadas) y Enter en la secundaria acepta. Así se digitan unidades (número, Enter,
// Enter) o cajas (Enter, número, Enter) sin flechas. Tab / ↑ ↓ también cambian de campo; Esc cancela
// sin tocar la línea. En Unidad primaria también vale «5c».
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Presentacion } from '../api/contratos';
import { Modal } from '../comun/Modal';
import { decimal, entradaDecimal } from '../comun/formato';
import { leerCantidad, problemaMultiplo, type Equivalencia } from './cantidad';

/** «119 CJ + 6 UN»: cajas COMPLETAS y unidades sueltas (más fácil de leer que «119,67 CJ»). */
function enCajas(base: number, rtu: number, caja: string, unidad: string): string {
  const cajas = Math.floor(base / rtu + 1e-9);
  const sueltas = Number((base - cajas * rtu).toFixed(6));
  if (!cajas) return `${decimal(String(sueltas))} ${unidad}`;
  return sueltas
    ? `${decimal(String(cajas))} ${caja} + ${decimal(String(sueltas))} ${unidad}`
    : `${decimal(String(cajas))} ${caja}`;
}

/** Número para un campo editable: hasta 3 decimales, coma decimal, sin ruido binario. */
function campo(n: number): string {
  return Number.isFinite(n) && n > 0 ? String(Number(n.toFixed(3))).replace('.', ',') : '';
}

export function CantidadRtu({
  sku,
  nombre,
  unidad,
  presentaciones,
  cajas,
  inicial,
  reemplazar,
  multiplo,
  disponible,
  alAceptar,
  alCancelar,
}: {
  sku: string;
  nombre: string;
  unidad: string;
  presentaciones: readonly Presentacion[];
  /** Presentaciones secundarias vendibles (al menos una), de menor a mayor factor. */
  cajas: Presentacion[];
  /** Lo que había (o lo primero que se digitó sobre la celda), en unidad base. */
  inicial: string;
  /** true: se empezó a escribir sobre la celda; el cursor va al final en vez de seleccionar todo. */
  reemplazar: boolean;
  multiplo: string | null;
  disponible: string | null;
  alAceptar: (base: string, equivalencia: Equivalencia | null) => void;
  alCancelar: () => void;
}) {
  const [i, setI] = useState(0);
  const caja = cajas[i]!;
  const rtu = Number(caja.factor_a_base);
  const [prim, setPrim] = useState(inicial);
  const [sec, setSec] = useState(() => campo(Number(entradaDecimal(inicial) ?? 0) / rtu));
  // Qué campo escribió el ejecutivo por última vez: ese manda y el otro se deriva.
  const [origen, setOrigen] = useState<'prim' | 'sec'>('prim');
  const primRef = useRef<HTMLInputElement>(null);
  // Corre DESPUÉS de que el Modal enfoca el campo: si se empezó a escribir sobre la celda, el cursor
  // queda al final (para seguir escribiendo); si no, todo marcado (lo que se escriba lo reemplaza).
  useEffect(() => {
    const el = primRef.current;
    if (!el) return;
    if (reemplazar) el.setSelectionRange(el.value.length, el.value.length);
    else el.select();
  }, []);

  const leer = (): { base: string; equivalencia: Equivalencia | null } | { error: string } => {
    if (origen === 'sec') {
      const n = Number(entradaDecimal(sec) ?? NaN);
      if (!(n > 0)) return { error: `Cantidad de ${caja.codigo} inválida` };
      const base = String(Number((n * rtu).toFixed(6)));
      return {
        base,
        equivalencia: { codigo: caja.codigo, cantidad: String(n), factor: String(rtu) },
      };
    }
    const l = leerCantidad(prim, presentaciones, unidad);
    return l.ok ? { base: l.base, equivalencia: l.equivalencia } : { error: l.error };
  };
  const r = leer();
  const error = 'error' in r ? (prim || sec ? r.error : null) : problemaMultiplo(r.base, multiplo);
  const valido = !('error' in r) && !error;

  const aceptar = () => {
    if (valido && !('error' in r)) alAceptar(r.base, r.equivalencia);
  };
  const ir = (campo: string) => {
    const el = document.querySelector<HTMLInputElement>(`[data-rtu="${campo}"]`);
    el?.focus();
    el?.select();
  };
  const tecla = (e: KeyboardEvent<HTMLInputElement>, otro: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      // Primaria → baja a la secundaria; secundaria → acepta.
      if (otro === 'sec') ir('sec');
      else aceptar();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      ir(otro);
    }
  };
  const cambiarCaja = (j: number) => {
    setI(j);
    const f = Number(cajas[j]!.factor_a_base);
    const base = 'error' in r ? 0 : Number(r.base);
    setSec(campo(base / f));
    setOrigen('prim');
    setPrim(campo(base));
  };

  return (
    <Modal
      titulo={`Cantidad para unidades distintas · ${sku}`}
      ancho="angosto"
      alCerrar={alCancelar}
      devolverFoco={false}
      ubicacion="media"
      pie={
        <>
          <button type="button" onClick={alCancelar}>
            Cancelar
          </button>
          <button type="button" className="primario" disabled={!valido} onClick={aceptar}>
            Aceptar <kbd>Enter</kbd>
          </button>
        </>
      }
    >
      <div className="rtu">
        {/* Qué se está vendiendo y cuánto hay: arriba, de un vistazo. */}
        <div className="rtu-cab">
          <div className="rtu-prod">
            <span className="mono fuerte">{sku}</span>
            <span className="fuerte">{nombre}</span>
          </div>
          {disponible !== null && (
            <div className="rtu-stock" data-rtu-disponible>
              <span className="etiqueta-c21">Stock</span>
              <span className="mono fuerte">
                {decimal(disponible)} {unidad}
              </span>
              <span className="apagado">
                {enCajas(Number(disponible), rtu, caja.codigo, unidad)}
              </span>
            </div>
          )}
        </div>
        <div className="rtu-grilla">
          <label htmlFor="rtu-prim">Unidad primaria</label>
          <input
            id="rtu-prim"
            data-rtu="prim"
            data-autofoco
            className="celda-num fuerte"
            inputMode="decimal"
            aria-label="Unidad primaria"
            value={prim}
            ref={primRef}
            onChange={(e) => {
              const t = e.target.value;
              setPrim(t);
              setOrigen('prim');
              const l = leerCantidad(t, presentaciones, unidad);
              setSec(l.ok ? campo(Number(l.base) / rtu) : '');
            }}
            onKeyDown={(e) => tecla(e, 'sec')}
          />
          <span className="mono">{unidad}</span>

          <span>R.T.U.</span>
          <output className="mono rtu-factor" aria-label="R.T.U.">
            {decimal(caja.factor_a_base)}
          </output>
          <span className="apagado">
            1 {caja.codigo} = {decimal(caja.factor_a_base)} {unidad}
          </span>

          <label htmlFor="rtu-sec">Unidad secundaria</label>
          <input
            id="rtu-sec"
            data-rtu="sec"
            className="celda-num fuerte"
            inputMode="decimal"
            aria-label="Unidad secundaria"
            value={sec}
            onChange={(e) => {
              const t = e.target.value;
              setSec(t);
              setOrigen('sec');
              const n = Number(entradaDecimal(t) ?? NaN);
              setPrim(n > 0 ? campo(n * rtu) : '');
            }}
            onKeyDown={(e) => tecla(e, 'prim')}
          />
          {cajas.length > 1 ? (
            <select
              aria-label="Presentación secundaria"
              value={i}
              onChange={(e) => cambiarCaja(Number(e.target.value))}
            >
              {cajas.map((c, j) => (
                <option key={c.id} value={j}>
                  {c.codigo}
                </option>
              ))}
            </select>
          ) : (
            <span className="mono">{caja.codigo}</span>
          )}
        </div>
        {/* Lo que queda en la nota: abajo y destacado (o el problema, en rojo). */}
        {error ? (
          <p className="rtu-resultado texto-malo" role="alert">
            {error}
          </p>
        ) : !('error' in r) ? (
          <p className="rtu-resultado">
            <span>La línea queda en</span>{' '}
            <strong className="mono">
              {decimal(r.base)} {unidad}
            </strong>
            <span className="apagado">{enCajas(Number(r.base), rtu, caja.codigo, unidad)}</span>
          </p>
        ) : (
          <p className="rtu-resultado apagado">Escriba unidades o cajas.</p>
        )}
      </div>
    </Modal>
  );
}
